/**
 * analytics.ts — GA4 Custom Event Tracking
 * Centralizes GA4 event firing for Rebel Genomics SPA
 */

/**
 * Track custom events in GA4
 * Uses window.gtag if available
 */
export const trackEvent = (
  eventName: string,
  eventData?: Record<string, any>
) => {
  if (typeof window !== "undefined" && typeof (window as any).gtag === "function") {
    (window as any).gtag("event", eventName, eventData);
  }
};

/**
 * Track CTA clicks (WhatsApp, Contáctame, etc.)
 */
export const trackCTAClick = (ctaType: string, courseId?: string) => {
  trackEvent("cta_click", {
    cta_type: ctaType, // "whatsapp", "contact_form", "email"
    course_id: courseId,
    timestamp: new Date().toISOString(),
  });
};

/**
 * Track course enrollment CTAs (Inscribirse)
 */
export const trackEnrollmentClick = (courseId: string, courseName: string, coursePrice: number) => {
  trackEvent("enrollment_click", {
    course_id: courseId,
    course_name: courseName,
    course_price: coursePrice,
    timestamp: new Date().toISOString(),
  });
};

/**
 * Track scroll events (25%, 50%, 75%, 100%)
 */
export const trackScroll = (scrollPercentage: number) => {
  trackEvent("scroll_milestone", {
    scroll_percentage: scrollPercentage,
    timestamp: new Date().toISOString(),
  });
};

/**
 * Track form submissions
 */
export const trackFormSubmit = (formType: string, formName?: string) => {
  trackEvent("form_submit", {
    form_type: formType, // "email_waitlist", "contact_form", "whitepaper", "enrollment"
    form_name: formName,
    timestamp: new Date().toISOString(),
  });
};

/**
 * Track page views (called on route change in SPA)
 */
export const trackPageView = (pageName: string, pageTitle?: string) => {
  trackEvent("page_view", {
    page_name: pageName, // "home", "curso-online", "presencial", etc.
    page_title: pageTitle,
    timestamp: new Date().toISOString(),
  });
};

/**
 * Track video plays/engagement
 */
export const trackVideoEngagement = (videoTitle: string, action: "play" | "complete") => {
  trackEvent("video_engagement", {
    video_title: videoTitle,
    action: action,
    timestamp: new Date().toISOString(),
  });
};

/**
 * Track product views (course cards, landing cards)
 */
export const trackProductView = (courseId: string, courseName: string) => {
  trackEvent("view_item", {
    items: [
      {
        item_id: courseId,
        item_name: courseName,
        item_category: "course",
      },
    ],
  });
};

/**
 * Setup scroll tracking on window load
 * Fires events at 25%, 50%, 75%, 100% scroll
 */
export const setupScrollTracking = () => {
  const trackedScrolls = new Set<number>();

  window.addEventListener("scroll", () => {
    const windowHeight = window.innerHeight;
    const docHeight = document.documentElement.scrollHeight - windowHeight;
    const scrolled = window.scrollY / docHeight;
    const percentage = Math.round(scrolled * 100);

    const milestones = [25, 50, 75, 100];
    milestones.forEach((milestone) => {
      if (percentage >= milestone && !trackedScrolls.has(milestone)) {
        trackedScrolls.add(milestone);
        trackScroll(milestone);
      }
    });
  });
};

/**
 * Initialize GA4 event tracking for SPA
 * Call once in main.tsx or App.tsx
 */
export const initializeAnalytics = () => {
  // Setup scroll tracking
  setupScrollTracking();

  // Log initialization
  if (typeof window !== "undefined" && typeof (window as any).gtag === "function") {
    console.log("✅ GA4 Analytics initialized");
  } else {
    console.warn("⚠️ GA4 gtag not available");
  }
};
